import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import type { World, WorldRegion } from '@mezha/api'
import {
  useAcceptEncounterMutation, useCellActionMutation, useDeclineEncounterMutation, useEnterMutation, useLeaveMutation, useMoveMutation,
  useWorldQuery,
} from '../../api/endpoints/world'
import { useStoryQuery } from '../../api/endpoints/quests'
import { useHeroQuery } from '../../api/endpoints/hero'
import { useCommand } from '../../lib/useCommand'
import { countdown, useRefetchAt, useServerNow } from '../../lib/time'
import { usePhone, useSize } from '../../lib/useSize'
import { useShellMode } from '../../shell/ShellContext'
import { Emblem, type EmblemName } from '../../ui/brand/Emblems'
import { Failure, Loading } from '../../ui/State'
import { ActionButton, PlankButton, Sheet, cx } from '../../ui'
import { rewardText } from '../common'
import { CELL_BASE, DIRS, WorldMap, canStep, dirTo, visibleWindow, whereText, type CellEdge, type CellTile, type Dir, type MapCell } from './geometry'
import './map.css'
import { MapArt, type MapArtStatus } from './MapArt'
import { TERRAINS } from './artModel'
import { activeStoryQuest, questRoute } from '../questNavigation'
import '../questNavigation.css'

const ACTION_EMBLEM: Record<string, EmblemName> = { fish: 'map.fish', gather: 'map.herb', herb: 'map.herb' }
const actionEmblem = (id: string): EmblemName => ACTION_EMBLEM[id] ?? (id.includes('fish') ? 'map.fish' : 'map.herb')

/** The decorative frame overlays the grid instead of reserving an empty gutter. */
const FRAME = 0 // Decorative frame overlays the content; it consumes no layout gutter.

/** `/play/map`: the world around the hero — his region and the regions nearby on one grid, as many cells as the
 *  screen holds — a step to a neighbour (over a border too, with its toll), cell actions and the encounter on arrival. */
export function MapScreen() {
  // The journal folds into a line (as in the map prototypes); the grid fills the stage.
  useShellMode('window', 'scene')
  const world = useWorldQuery()
  const story = useStoryQuery()
  const heroSheet = useHeroQuery().data
  const w = world.data
  const phone = usePhone()
  const now = useServerNow(250)
  const [selected, setSelected] = useState<{ x: number; y: number } | null>(null)
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  // A step into fog without a known path asks first — on the cell itself (phones: in place of the d-pad).
  const [ask, setAsk] = useState<{ x: number; y: number } | null>(null)
  const [sheet, setSheet] = useState(false)
  const [artStatus, setArtStatus] = useState<MapArtStatus>('loading')
  const legacyArt = new URLSearchParams(window.location.search).get('mapArt') === 'legacy'
  const [boxRef, avail] = useSize<HTMLDivElement>()
  const [move] = useMoveMutation()
  const [decline] = useDeclineEncounterMutation()
  const cmd = useCommand(() => void world.refetch())

  useRefetchAt(w?.travel?.arrives_at, world.refetch)
  useRefetchAt(w?.activity?.completes_at, world.refetch)
  useEffect(() => { setSelected(null); setAsk(null); setHover(null) }, [w?.x, w?.y])

  const travelling = w?.travel ?? null
  const arrived = travelling ? countdown(now, travelling.arrives_at, travelling.departs_at) : null
  const map = useMemo(() => (w ? new WorldMap(w) : null), [w])
  const view = useMemo(() => (w && map ? visibleWindow(map.bounds, { x: w.x, y: w.y }, { w: Math.max(avail.w - FRAME, 132), h: Math.max(avail.h - FRAME, 132) }, phone,
    ['gorsten','raskolye'].includes(w.region.id) ? {x0:w.region.origin_x,y0:w.region.origin_y,cols:w.region.width,rows:w.region.height} : undefined) : null),
    [w, map, avail.w, avail.h, phone])

  const here = w && map ? map.at(w.x, w.y) : undefined
  const [params] = useSearchParams()
  const destination = useMemo(() => params.has("destination") && params.has("x") && params.has("y") ? { x: Number(params.get("x")), y: Number(params.get("y")), place_id: params.get("place") ?? "", place_name: params.get("destination") ?? "", object_name: "нужную метку", title: "", region_name: "мир" } : null, [params])
  const activeQuest = activeStoryQuest(story.data)
  const questStep = destination ?? activeQuest?.current ?? null
  const route = useMemo(() => (w && map && questStep ? questRoute(map, w, questStep) : null), [w, map, questStep])
  const questGoal = map && questStep ? map.at(questStep.x, questStep.y) : undefined
  const questNext = route?.[0]
  const questDir = w && questNext ? dirTo(w, questNext) : null
  const shown = hover ?? selected
  const target = map && shown ? map.at(shown.x, shown.y) : here
  // A heavy injury after a defeat keeps the hero from moving (owner 28.09.2026).
  const injury = heroSheet?.injury
  const heavy = injury?.heavy && injury.heavy_until && countdown(now, injury.heavy_until).ms > 0 ? injury.heavy_until : null
  useRefetchAt(heavy, world.refetch)
  const blocked = Boolean(travelling || w?.encounter || w?.in_combat || w?.activity || heavy)

  const step = async (c: MapCell | undefined) => {
    if (!w || !c) return
    setAsk(null)
    setSheet(false)
    setSelected({ x: c.x, y: c.y })
    await cmd.run(() => move({ expected_version: w.version, x: c.x, y: c.y }).unwrap())
  }
  /** A press on a neighbour steps at once; fog without a known path asks on the cell first. */
  const tryStep = (c: MapCell | undefined) => {
    if (!w || !c || blocked || !canStep(w, c)) return
    if (astray(c) && !(ask && ask.x === c.x && ask.y === c.y)) {
      setAsk({ x: c.x, y: c.y })
      setSelected({ x: c.x, y: c.y })
      return
    }
    void step(c)
  }
  const stepDir = (d: Dir) => {
    if (!w || !map) return
    tryStep(map.at(w.x + DIRS[d].dx, w.y + DIRS[d].dy))
  }
  // Arrow keys step like the d-pad (not while typing in the chat).
  const arrows = useRef<(e: KeyboardEvent) => void>(() => {})
  arrows.current = e => {
    if (e.target instanceof HTMLElement && e.target.closest('input, textarea, select, [role="dialog"], [role="tablist"]')) return
    const d = ({ ArrowUp: 'n', ArrowDown: 's', ArrowLeft: 'w', ArrowRight: 'e' } as Record<string, Dir>)[e.key]
    if (!d) return
    e.preventDefault()
    stepDir(d)
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => arrows.current(e)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const [accept] = useAcceptEncounterMutation()
  const navigate = useNavigate()
  // A closed or vanished encounter re-reads the world; too_weak keeps it waiting.
  const enc = useCommand(() => void world.refetch(), e => { if (e.code === 'encounter_closed' || e.code === 'encounter_not_found') void world.refetch() })
  const onAccept = async () => {
    const e = w?.encounter
    if (!w || !e) return
    const r = await enc.run(() => accept({ expected_version: w.version, encounter_id: e.id }).unwrap())
    if (r) navigate(`/play/battle/${r.battle_id}`, { state: { from: '/play/map' } })
  }

  if (world.isLoading) return <div className="stage-scene map"><div className="mapscene" /><div className="map__state"><Loading text="Разворачиваем карту…" /></div></div>
  if (!w || !view || !map) {
    return (
      <div className="stage-scene map"><div className="mapscene" />
        <div className="map__state"><Failure error={world.error} retry={() => void world.refetch()} what="Карта" /></div>
      </div>
    )
  }

  if (!map.regions.some(r => r.cells.length > 0)) return <div className="stage-scene map"><div className="map__state"><p>Нет известных клеток карты.</p><PlankButton onClick={() => void world.refetch()}>Повторить</PlankButton></div></div>

  const select = (c: MapCell) => {
    setAsk(null)
    setSelected({ x: c.x, y: c.y })
    if (phone) setSheet(true)
  }

  // Rows of the window; a world point outside every region is empty ground.
  const rows: Array<Array<MapCell | { x: number; y: number; void: true }>> = []
  for (let y = view.y0; y < view.y0 + view.rows; y++) {
    const row: Array<MapCell | { x: number; y: number; void: true }> = []
    for (let x = view.x0; x < view.x0 + view.cols; x++) row.push(map.at(x, y) ?? { x, y, void: true })
    rows.push(row)
  }
  const hero = { x: w.x, y: w.y }
  const press = (c: MapCell) => (!blocked && canStep(hero, c) ? tryStep(c) : select(c))
  /** The sides of a cell that lie on a region border (a dashed line marks them). */
  const edges = (c: MapCell) => (['n', 's', 'w', 'e'] as Dir[]).filter(d => {
    const n = map.at(c.x + DIRS[d].dx, c.y + DIRS[d].dy)
    return n && n.region.id !== c.region.id
  })
  const labels = regionLabels(map.regions.filter(r => r.id !== w.region.id), view)
  const failed = (c: typeof cmd): Failed => c.text
  const onDecline = () => {
    const e = w.encounter
    if (e) void enc.run(() => decline({ expected_version: w.version, encounter_id: e.id }).unwrap())
  }
  const card = <CellCard world={w} map={map} cell={target} now={now} error={failed(cmd)} encounterError={failed(enc)} onDecline={onDecline} onAccept={onAccept}
    onStep={c => tryStep(c)} asking={Boolean(ask && target && ask.x === target.x && ask.y === target.y)} />
  const askCell = ask ? map.at(ask.x, ask.y) : undefined
  const goal = travelling ? map.at(travelling.to_x, travelling.to_y) : undefined

  return (
    <div className="stage-scene map">
      <div className="mapscene" aria-hidden="true" />
      <div className="regionplate">
        <h1><b>{w.region.name}</b></h1>
        <img className="flourish" src="/assets/ui/divider.webp" alt="" aria-hidden="true" />
        <span>{heavy ? `тяжёлая травма: идти нельзя ещё ${countdown(now, heavy).text}` : travelling?.lost ? 'идём в туман наугад' : travelling?.to_region_name ? `идём в «${travelling.to_region_name}»: ${goal?.name ?? ''}` : travelling ? `идём к клетке «${goal?.name ?? ''}»` : `вы: ${here?.name ?? ''} (${w.x}, ${w.y})${w.lost ? ' — туман сбил с пути' : ''}`}</span>
      </div>
      {(destination || activeQuest) && questStep && (
        <aside className="quest-route" aria-label="Путь к цели задания">
          <b>{destination ? "Путь" : activeQuest?.title}: {questStep.place_name}</b>
          <span>{w.x === questStep.x && w.y === questStep.y
            ? (w.place_id === questStep.place_id
                ? `Вы на месте — выберите «${questStep.object_name || questStep.title}»`
                : w.place_id
                  ? `Выйдите на клетку и войдите в «${questStep.place_name}»`
                  : `Вы на нужной клетке — войдите в «${questStep.place_name}»`)
            : questDir && questNext
              ? `Следующий шаг: ${DIRS[questDir].label.toLowerCase()} — «${questNext.name}» · осталось ${route?.length ?? 0}`
              : questGoal
                ? 'Из текущей клетки нет известного проходимого маршрута'
                : `Цель: ${questStep.region_name} · клетка ${questStep.x}, ${questStep.y}`}</span>
        </aside>
      )}
      <div className="map__field" ref={boxRef}>
        <div className={cx('grid iron', view.cs < 64 && 'grid--compact', !legacyArt && 'grid--enhanced', artStatus === 'ready' && !legacyArt && 'grid--layered')} role="grid" aria-label={`Карта: ${map.regions.map(r => r.name).join(', ')}`} style={{ '--cs': `${view.cs}px`, '--k': (view.cs / CELL_BASE).toFixed(3), gridTemplateColumns: `repeat(${view.cols}, ${view.cs}px)` } as CSSProperties}>
          {!legacyArt && <MapArt map={map} view={view} onStatus={setArtStatus} />}
          {!legacyArt && artStatus === 'loading' && <div className="map-art__loading" role="status">Разворачиваем карту…</div>}
          {shown && target && !ask && !sheet && <span className="map__active-label" aria-hidden="true" style={{
            left: Math.max(6, Math.min((target.x - view.x0 + .5) * view.cs - 120, view.cols * view.cs - 246)),
            top: Math.max(6, Math.min((target.y - view.y0 + 1) * view.cs + 3, view.rows * view.cs - 54)),
            maxWidth: Math.min(240, view.cols * view.cs - 12),
          }}>{target.places[0]?.name ?? target.name}<small>{target.x}, {target.y}</small></span>}
          {labels.map(l => (
            <span key={l.id} className="regionmark" aria-hidden="true" style={{ left: l.left * view.cs, top: l.top * view.cs, width: l.width * view.cs } as CSSProperties}>
              <span className="plank"><span className="plank__in">{l.name}</span></span>
            </span>
          ))}
          {rows.map((row, i) => (
            <div role="row" key={i} className="grid__row">
              {row.map(c => {
                if ('void' in c) return <span key={`${c.x},${c.y}`} className="cell cell--void" aria-hidden="true" />
                const isHero = c.x === hero.x && c.y === hero.y
                const reach = !blocked && canStep(hero, c)
                const dir = reach ? dirTo(hero, c) : null
                const eta = travelling && c.x === travelling.to_x && c.y === travelling.to_y
                const isSel = selected ? selected.x === c.x && selected.y === c.y : false
                const asking = Boolean(ask && ask.x === c.x && ask.y === c.y)
                const isQuestGoal = Boolean(questStep && c.x === questStep.x && c.y === questStep.y)
                const isQuestNext = Boolean(questNext && c.x === questNext.x && c.y === questNext.y)
                const across = c.region.id !== w.region.id
                const toll = across && c.toll ? c.toll : null
                const label = `${c.name} (${c.x}, ${c.y})${across ? `, ${c.region.name}` : ''}${isHero ? ', вы здесь' : ''}${isQuestGoal ? `, цель задания: ${questStep?.place_name}` : ''}${isQuestNext ? ', следующий шаг к цели задания' : ''}${c.locked ? ', закрыто' : ''}${!c.passable ? ', не пройти' : ''}${c.encounters ? ', риск встречи' : ''}${toll ? `, мыт ${toll.silver} серебра` : ''}${c.fog ? `, туман: ${c.fog.known ? 'путь известен' : 'пути не знаете'}` : ''}`
                const cost = `${astray(c) ? c.travel_seconds * 2 : c.travel_seconds} с${toll ? `, мыт ${toll.silver}` : ''}`
                return (
                  <button key={`${c.x},${c.y}`} type="button" role="gridcell" aria-selected={isSel}
                    aria-label={reach ? `${label}. ${astray(c) ? (asking ? `Нажмите ещё раз: шаг наугад, ${cost}` : `Шаг в туман наугад, ${cost}`) : `Шаг, ${cost}`}` : label}
                    className={cx('cell', TERRAINS.has(c.terrain) && 'cell--art', reach && 'is-reach', isQuestGoal && 'is-quest-goal', isQuestNext && 'is-quest-next', (isSel || eta || asking) && 'is-selected', asking && 'is-asking', isHero && w.encounter && 'is-danger', c.locked && 'is-locked', c.fog && 'is-fog', c.fog?.known && 'is-fog-known')}
                    onClick={() => press(c)}
                    onMouseEnter={phone ? undefined : () => setHover({ x: c.x, y: c.y })} onMouseLeave={phone ? undefined : () => setHover(null)}
                    onContextMenu={e => { e.preventDefault(); select(c); setSheet(true) }}>
                    <CellTileLayer tile={map.tile(c)} />
                                        {c.places.length > 0 && <Emblem name="map.place" className="deco gate" />}
                    {c.actions.length > 0 && <span className="act" aria-hidden="true"><Emblem name={actionEmblem(c.actions[0])} /></span>}
                    {c.encounters && <span className="risk" aria-hidden="true"><Emblem name="map.danger" /></span>}
                    {c.toll && <span className="toll" aria-hidden="true"><Emblem name="map.toll" /></span>}
                    {isQuestGoal && <span className="cell__quest cell__quest--goal" aria-hidden="true"><Emblem name="loc.task" />цель</span>}
                    {isQuestNext && !isQuestGoal && <span className="cell__quest cell__quest--next" aria-hidden="true"><Emblem name="map.travel" />к цели</span>}
                    {edges(c).map(d => <span key={d} className={`rborder rborder--${d}`} aria-hidden="true" />)}
                    {c.locked && <span className="lockmark" aria-hidden="true"><Emblem name="map.lock" /></span>}
                    {dir && <span className={`arrow arrow--${dir}`} aria-hidden="true" />}
                    {dir && view.cs >= 56 && !asking && <span className={cx('cell__eta', dir === 's' && 'cell__eta--low')} aria-hidden="true">{astray(c) ? `наугад ${c.travel_seconds * 2} с` : `${c.travel_seconds} с`}{toll ? ` · ${toll.silver} сер.` : ''}</span>}
                    {asking && !phone && (
                      <span className="cellask" aria-hidden="true"><b>Пути не знаете</b><small>наугад {c.travel_seconds * 2} с</small><span className="cellask__go">Шагнуть ▸</span></span>
                    )}
                    {isHero && !eta && <span className="hero" aria-hidden="true"><span className="medal">{heroSheet?.portrait ? <img src={heroSheet.portrait} alt="" /> : <Emblem name="nav.hero" size={38} />}</span></span>}
                    {eta && arrived && (
                      <span className="eta" aria-hidden="true"><span className="timer" style={{ '--p': `${Math.round(arrived.passed * 100)}%` } as CSSProperties}><b>{arrived.ms > 0 ? arrived.text : '…'}</b></span></span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      {phone && w.encounter ? (
        <div className="dpad dpad--ask" role="group" aria-label="Встреча на клетке">
          <span><b>Нападение: {w.encounter.name}, ур. {w.encounter.level}</b><small role={enc.text ? 'alert' : undefined}>{enc.text ?? (w.encounter.telegraph ? `«${w.encounter.telegraph}»` : 'встреча выпала при прибытии')}</small></span>
          <PlankButton onClick={onDecline}>Отказаться</PlankButton>
          <ActionButton gold onPress={onAccept}>В бой</ActionButton>
        </div>
      ) : phone && askCell ? (
        <div className="dpad dpad--ask" role="group" aria-label="Шаг в туман наугад">
          <span><b>{askCell.name}: пути не знаете</b><small>можно заблудиться · {askCell.travel_seconds * 2} с · {w.step_cost * 2} выносл.</small></span>
          <PlankButton onClick={() => setAsk(null)}>Отмена</PlankButton>
          <PlankButton gold onClick={() => void step(askCell)}>Наугад ▸</PlankButton>
        </div>
      ) : (
      <div className="dpad" aria-label="Шаг в сторону">
        {(['w', 'n', 's', 'e'] as Dir[]).map(d => {
          const c = map.at(w.x + DIRS[d].dx, w.y + DIRS[d].dy)
          const ok = !blocked && canStep(hero, c)
          return (
            <PlankButton key={d} gold={questDir === d} locked={!ok} onClick={() => stepDir(d)} aria-label={`${questDir === d ? 'К цели, ' : ''}шаг: ${DIRS[d].label}${c ? `, ${c.name}` : ''}`}>
              {questDir === d ? `К цели ${DIRS[d].arrow}` : d === 'e' ? `${DIRS[d].label} ${DIRS[d].arrow}` : `${DIRS[d].arrow} ${DIRS[d].label}`}
            </PlankButton>
          )
        })}
      </div>
      )}
      {!phone && <div className="map__card">{card}<p className="map__tip"><Emblem name="loc.inspect" size={16} />Наведите на клетку — карточка покажет её; нажмите соседнюю — герой пойдёт.</p></div>}
      {phone && (
        <Sheet open={sheet} title={w.encounter ? 'Встреча на клетке' : target?.name ?? ''} emblem={w.encounter ? 'map.danger' : 'nav.map'} onClose={() => setSheet(false)}>
          {card}
        </Sheet>
      )}
    </div>
  )
}

/** A step into fog whose path the hero does not know goes astray (twice the time and stamina). */
const astray = (c: MapCell) => Boolean(c.fog && !c.fog.known)

type Label = { id: string; name: string; left: number; top: number; width: number }

/** Name plates of the regions nearby, over the top of their visible part (in cells of the window). */
function regionLabels(regions: WorldRegion[], view: { x0: number; y0: number; cols: number; rows: number }): Label[] {
  const out: Label[] = []
  for (const r of regions) {
    const x0 = Math.max(view.x0, r.origin_x)
    const x1 = Math.min(view.x0 + view.cols, r.origin_x + r.width)
    const y0 = Math.max(view.y0, r.origin_y)
    const y1 = Math.min(view.y0 + view.rows, r.origin_y + r.height)
    if (x1 - x0 < 2 || y1 <= y0) continue
    out.push({ id: r.id, name: r.name, left: x0 - view.x0, top: y0 - view.y0, width: x1 - x0 })
  }
  return out
}

type Failed = string | null
type CardProps = {
  world: World; map: WorldMap; cell: MapCell | undefined; now: number; error: Failed; encounterError: Failed
  onDecline: () => void; onAccept: () => Promise<unknown>; onStep: (c: MapCell) => void; asking: boolean
}

function CellCard({ world: w, map, cell, now, error, encounterError: encErr, onDecline, onAccept, onStep, asking }: CardProps) {
  const navigate = useNavigate()
  const [act] = useCellActionMutation()
  const [enter, { isLoading: entering }] = useEnterMutation()
  const [leave] = useLeaveMutation()
  const cmd = useCommand()
  const shown = error ?? cmd.text
  const [placeChoice, setPlaceChoice] = useState<{ cell: string; id: string } | null>(null)
  const cellKey = cell ? `${cell.region.id}:${cell.x}:${cell.y}` : ''
  const placeId = cell?.places.find(p => placeChoice?.cell === cellKey && p.id === placeChoice.id)?.id ?? cell?.places[0]?.id

  if (w.encounter) {
    const e = w.encounter
    return (
      <div className="cellcard">
        <div className="quest__lintel"><div className="plank"><h2 className="plank__in">Нападение!</h2></div></div>
        <div className="quest__sheet parch">
          <div className="cellcard__type">{cell?.name ?? ''} · встреча выпала при прибытии</div>
          <div className="cellfacts">
            <div className="cellfact is-warn"><Emblem name="battle.foe" />{e.name}, ур. {e.level}<span>{e.tier === 'normal' ? '' : e.tier}</span></div>
            {e.telegraph && <div className="cellfact"><Emblem name="loc.inspect" />«{e.telegraph}»</div>}
            {e.description && <p className="note map__note">{e.description}</p>}
          </div>
          {encErr && <p className="map__error" role="alert">{encErr}</p>}
          <div className="actions">
            <ActionButton gold onPress={onAccept}><Emblem name="act.strike" size={16} />В бой</ActionButton>
            <PlankButton onClick={onDecline}>Отказаться</PlankButton>
          </div>
        </div>
      </div>
    )
  }
  if (!cell) return null
  const here = cell.x === w.x && cell.y === w.y
  const adjacent = Boolean(dirTo(w, cell))
  const terrain = map.terrain(cell)
  const across = cell.region.id !== w.region.id
  const toll = across && cell.toll ? cell.toll : null
  const defs = cell.actions.map(id => cell.region.actions.find(a => a.id === id) ?? { id, name: id, duration_seconds: 0 })
  const last = w.last_action && now - Date.parse(w.last_action.completed_at) < 10 * 60_000 ? w.last_action : null
  const busy = Boolean(w.travel || w.activity || w.in_combat)

  return (
    <div className="cellcard">
      <div className="quest__lintel"><div className="plank"><h2 className="plank__in">{cell.name}</h2></div></div>
      <div className="quest__sheet parch">
        <div className="cellcard__type">{across ? `${cell.region.name} · ` : ''}{terrain?.name ?? cell.terrain} · клетка ({cell.x}, {cell.y}) · {whereText(w, cell)}</div>
        <div className="cellfacts">
          {cell.locked && <div className="cellfact is-warn"><Emblem name="map.lock" />Закрыто — откроется по сюжету</div>}
          {!cell.passable && <div className="cellfact"><Emblem name="map.lock" />Не пройти</div>}
          {here && w.lost && (
            <div className="cellfact is-warn"><Emblem name="map.fog" />Туман сбил с пути
              <small className="cellfact__note">шли к «{map.at(w.lost.intended_x, w.lost.intended_y)?.name ?? ''}» ({w.lost.intended_x}, {w.lost.intended_y})</small></div>
          )}
          {cell.fog && (
            <div className={cx('cellfact', !cell.fog.known && 'is-warn')}><Emblem name={cell.fog.known ? 'map.fog_known' : 'map.fog'} />
              {cell.fog.known ? `Туман: путь «${cell.fog.path_name}» известен` : `Туман: ${cell.fog.path_name} — пути не знаете`}
              {!cell.fog.known && <small className="cellfact__note">можно заблудиться: шаг вдвое дольше и дороже</small>}</div>
          )}
          {cell.passable && !here && (
            <div className="cellfact"><Emblem name="map.travel" />{adjacent ? (astray(cell) ? 'Шаг в туман наугад' : 'Шаг сюда') : 'Путь по шагам'}
              <span>{astray(cell) ? `${cell.travel_seconds * 2} с · ${w.step_cost * 2} выносл.` : `${cell.travel_seconds} с · ${w.step_cost} выносл.`}</span></div>
          )}
          <div className="cellfact"><Emblem name="res.stamina" />Выносливость<span>{w.stamina.value} из {w.stamina.max}</span></div>
          {defs.map(a => (
            <div className="cellfact" key={a.id}><Emblem name={actionEmblem(a.id)} />{a.name}<span>{a.duration_seconds} с · {w.action_cost} выносл.</span></div>
          ))}
          <div className="cellfact"><Emblem name={cell.encounters ? 'map.danger' : 'map.safe'} />{cell.encounters ? 'Здесь бывают встречи' : 'Встреч здесь нет'}</div>
          {across && <div className="cellfact"><Emblem name="nav.location" />Другая область: {cell.region.name}<span>шаг через границу</span></div>}
          {toll && <div className="cellfact"><Emblem name="map.toll" />Мыт: {toll.silver} серебра<span>{toll.to || 'за проход'}</span></div>}
          {cell.places.map(p => <div className="cellfact" key={p.id}><Emblem name="map.place" />{p.name}</div>)}
          {here && w.activity && <div className="cellfact"><Emblem name={actionEmblem(w.activity.action)} />{w.activity.name}<span>до конца {countdown(now, w.activity.completes_at).text}</span></div>}
          {here && w.activity && ['fish', 'gather'].includes(w.activity.action) && <p className="note"><Link to="/play/inventory">Применить узел скорого дела из сумы</Link></p>}
          {here && last && <div className="cellfact"><Emblem name="reward.item" />{last.name}: {rewardText(last.reward) || 'ничего'}</div>}
        </div>
        {shown && <p className="map__error" role="alert">{shown}</p>}
        {w.place_id && (
          <>
            <p className="note map__note">Вы внутри места «{w.place?.name ?? w.place_id}»: шаги по карте — после выхода.</p>
            <div className="actions"><PlankButton onClick={() => void cmd.run(() => leave({ expected_version: w.version }).unwrap())}>Выйти из места</PlankButton></div>
          </>
        )}
        {!here && adjacent && cell.passable && !w.place_id && (
          <div className="actions">
            <PlankButton gold locked={busy} onClick={() => onStep(cell)}>
              <Emblem name="map.travel" size={16} />{astray(cell) ? (asking ? `Да, наугад · ${cell.travel_seconds * 2} с` : `Шагнуть наугад · ${cell.travel_seconds * 2} с`) : `Шаг · ${cell.travel_seconds} с`}{toll ? ` · мыт ${toll.silver}` : ''}
            </PlankButton>
          </div>
        )}
        {here && !w.place_id && (
          <div className="actions">
            {defs.map(a => (
              <PlankButton key={a.id} locked={busy} onClick={() => void cmd.run(() => act({ expected_version: w.version, action: a.id }).unwrap())}>
                <Emblem name={actionEmblem(a.id)} size={16} />{a.name}
              </PlankButton>
            ))}
            {placeId && <div className="map-place-entry">
              {cell.places.length > 1 && <label className="map-place-entry__choice">Место
                <select value={placeId} disabled={busy || entering} onChange={e => setPlaceChoice({ cell: cellKey, id: e.target.value })}>
                  {cell.places.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>}
              <PlankButton locked={busy || entering} onClick={async () => { if (await cmd.run(() => enter({ expected_version: w.version, place_id: placeId }).unwrap())) navigate('/play') }}>
                <Emblem name="loc.enter" size={16} />Войти
              </PlankButton>
            </div>}
          </div>
        )}
      </div>
      <Legend map={map} region={cell?.region} />
    </div>
  )
}

/** The picture of a cell under its marks, turned for a road tile by shape,
 *  and the neighbours' tiles running over its edges through ragged masks. */
function CellTileLayer({ tile }: { tile: CellTile }) {
  return (
    <>
      {tile.url && <span className="cell__tile" data-rotate={tile.rotate} style={{ ...tileStyle(tile.url), transform: tile.rotate ? `rotate(${tile.rotate}deg)` : undefined }} aria-hidden="true" />}
      {tile.edges.map(e => <span key={e.side} className="cell__edge" data-side={e.side} style={edgeStyle(e)} aria-hidden="true" />)}
    </>
  )
}

/** A narrow edge (a road, a village keeps its middle) is the same mask squeezed to its side. */
const NARROW = 0.55
function edgeStyle(e: CellEdge): CSSProperties {
  const mask = `url(/assets/map/edge-${e.side}-${e.variant}.png)`
  const n = `${NARROW * 100}%`
  const vert = e.side.includes('n') ? 'top' : e.side.includes('s') ? 'bottom' : 'center'
  const horiz = e.side.includes('w') ? 'left' : e.side.includes('e') ? 'right' : 'center'
  const size = !e.narrow ? '100% 100%' : e.side === 'n' || e.side === 's' ? `100% ${n}` : e.side === 'e' || e.side === 'w' ? `${n} 100%` : `${n} ${n}`
  const pos = `${horiz} ${vert}`
  return { ...tileStyle(e.url), WebkitMaskImage: mask, maskImage: mask, WebkitMaskSize: size, maskSize: size, WebkitMaskPosition: pos, maskPosition: pos }
}

/** A map tile as a cell picture or a legend swatch. */
const tileStyle = (url: string): CSSProperties | undefined => (url ? { backgroundImage: `url(${url})`, backgroundSize: 'cover' } : undefined)

function Legend({ map, region }: { map: WorldMap; region?: WorldRegion }) {
  // Content may include reusable terrain definitions not used in this region.
  const regions = region ? [region] : map.regions
  const terrains = [...new Map(regions.flatMap(r => r.terrains.filter(t => r.cells.some(c => c.terrain === t.id))).map(t => [t.id, t] as const)).values()]
  const cells = regions.flatMap(r => r.cells)
  return (
    <ul className="legend" aria-label="Обозначения">
      {terrains.map(t => (
        <li key={t.id}><i style={tileStyle(t.shapes ? t.shapes.straight : t.tile)} aria-hidden="true" />{t.name}{t.passable ? ` ${t.travel_seconds} с` : ' — не пройти'}</li>
      ))}
      <li><Emblem name="map.danger" size={15} />риск встречи</li>
      {cells.some(c => c.fog) && <li><i className="is-fog" aria-hidden="true" />туман: без пути можно заблудиться</li>}
      {map.regions.length > 1 && <li><i className="is-border" aria-hidden="true" />граница области{cells.some(c => c.toll) ? ' · мыт за переход' : ''}</li>}
    </ul>
  )
}
